import {html, HTMLTemplateResult} from "lit";
import {state} from "lit/decorators/state.js";
import {createRef, ref} from "lit/directives/ref.js";
import {when} from "lit/directives/when.js";
import {DialogCommand} from "./dialog-command.interface";
import {property} from "lit/decorators/property.js";
import {DialogCommandName} from "./dialog-command.enum";
import {AbstractLitElement} from "../abstract-lit.element";

export abstract class AbstractDialogComponent extends AbstractLitElement {

    @property({type: Boolean})
    public maximizable = false;

    @property({type: Boolean})
    public minimizable = false;

    @property({type: Boolean})
    public closable = false;

    @property({type: String})
    public title = `empty dialog title`;

    @state()
    protected _isDirty = false;

    private readonly _readyAsync;

    private _readyResolve!: (value: (HTMLDialogElement | PromiseLike<HTMLDialogElement>)) => void;

    private _dialogRef = createRef<HTMLDialogElement>();

    public constructor() {
        super();
        this._readyAsync = new Promise<HTMLDialogElement>(resolve => this._readyResolve = resolve);
    }

    protected get _hasHeaderButtons(): boolean {
        return this.maximizable
            || this.minimizable
            || this.closable;
    }

    private get _dialog(): Promise<HTMLDialogElement> {
        return this._readyAsync;
    }

    public render(): HTMLTemplateResult {
        return html`
            <backdrop></backdrop>
            <dialog ${ref(this._dialogRef)}>
                <header class="dialog__header">
                    ${this._renderHeaderTitle()}
                    ${when(this._hasHeaderButtons, () => html`
                        <div class="dialog__header__buttons">
                            ${when(this.minimizable, () => html`
                                <i class="header__button-minimize"
                                   @click=${this._onClickMinimize}></i>
                            `)}
                            ${when(this.maximizable, () => html`
                                <i class="header__button-maximize"
                                   @click=${this._onClickMaximize}></i>
                            `)}
                            ${when(this.closable, () => html`
                                <i class="header__button-close"
                                   @click=${this.onClickClose}></i>
                            `)}
                        </div>
                    `)}

                </header>
                <main class="dialog__main">
                    ${this._renderSidebarLeftWrapper()}
                    ${this._renderMainHeaderWrapper()}
                    ${this._renderMainSidebarLeftWrapper()}
                    ${this._renderMainContent()}
                </main>
                ${this._renderFooterWrapper()}
            </dialog>
        `;
    }

    public open(): void {
        this._dialog.then(() => this._dialogRef.value?.show());
    }

    public async close(): Promise<void> {
        this.dispatchEvent(
            new CustomEvent<{ value: any }>(`dialog-closed`)
        );

        return this._performClose();
    }

    protected firstUpdated(): void {
        this._readyResolve(this._dialogRef.value!);
    }

    protected abstract _renderMainContent(): unknown;

    protected _renderHeader(): unknown {
        return null;
    }

    protected _renderSidebarLeft(): unknown {
        return null;
    }

    protected _renderFooter(): unknown {
        return null;
    }

    protected _renderMainHeader(): unknown {
        return null;
    }

    protected _renderMainSidebarLeft(): unknown {
        return null;
    }

    protected onClickClose(): void {
        this.dispatchCommand<void>({
            command: DialogCommandName.CLOSE,
            payload: void 0
        })
    }

    protected dispatchCommand<T>(command: DialogCommand<T>): void {
        this.dispatchEvent(
            new CustomEvent<{ value: DialogCommand<T> }>(`dialog.message`, {detail: {value: command}})
        );
    }

    private _performClose(): Promise<void> {
        return new Promise(resolve => {
            setTimeout(() => {
                if (this._dialogRef.value?.open) {
                    this._dialogRef.value.close();
                }
                resolve();
            }, 450);
        });
    }

    private _renderHeaderTitle(): unknown {
        return html`
            <div class="dialog__header__title">
                ${this._renderHeader() ?? this.title}${when(this._isDirty, () => html`<span>&#129365;</span>`)}
            </div>`;
    }

    private _renderMainHeaderWrapper(): unknown {
        const renderedHeader = this._renderMainHeader();
        return (!renderedHeader)
            ? null
            : html`
                    <header class="main">${renderedHeader}</header>`
    }

    private _renderSidebarLeftWrapper(): unknown {
        const renderedSidebar = this._renderSidebarLeft();
        return (!renderedSidebar)
            ? null
            : html`
                    <aside>${renderedSidebar}</aside>`
    }

    private _renderMainSidebarLeftWrapper(): unknown {
        const renderedSidebar = this._renderMainSidebarLeft();
        return (!renderedSidebar)
            ? null
            : html`
                    <aside class="aside__main">${renderedSidebar}</aside>`
    }

    private _renderFooterWrapper(): unknown {
        const renderedFooter = this._renderFooter();
        return (!renderedFooter)
            ? null
            : html`
                    <footer class="dialog__footer">${renderedFooter}</footer>`
    }

    private _onClickMinimize(): void {
        this._resetDialogClassList();
        this._dialogRef.value?.classList.add(`dialog__minimized`);
    }

    private _onClickMaximize(): void {
        this._resetDialogClassList();
        this._dialogRef.value?.classList.add(`dialog__maximized`);
    }

    private _resetDialogClassList(): void {
        this._dialogRef.value?.classList.add(...Array.from(this._dialogRef.value?.classList ?? []).filter(className => !className.startsWith(`dialog__`)));
    }
}